import * as React from 'react'
import { StyleSheet, Text, View } from 'react-native'
import { useRouter } from 'expo-router'
import { Pressable, TextInput } from 'react-native-gesture-handler'
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useQuery, useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { DS } from '../../../design-system'
import { useStore } from '../../../models'
import { useState } from 'react'

type LoungeRow = {
  _id: string
  name: string
  status: string
  settings: {
    chatEnabled: boolean
    adHocGamesEnabled: boolean
    maxMembers: number
    chatRetentionDays: number
  }
  joinCode?: string
}

const columnHelper = createColumnHelper<LoungeRow>()

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: (info) => <DS.Text variant="Body Small/Medium">{info.getValue()}</DS.Text>
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (info) => (
      <View style={[styles.statusBadge, info.getValue() === 'active' ? styles.statusActive : styles.statusInactive]}>
        <DS.Text variant="Mini/Bold" style={{ color: '#fff' }}>
          {info.getValue().toUpperCase()}
        </DS.Text>
      </View>
    )
  }),
  columnHelper.accessor('settings', {
    header: 'Max Members',
    cell: (info) => <DS.Text variant="Body Small/Regular">{info.getValue().maxMembers}</DS.Text>
  }),
  columnHelper.accessor('joinCode', {
    header: 'Join Code',
    cell: (info) => (
      <DS.Text selectable variant="Body Small/Regular" style={{ fontFamily: 'monospace' }}>
        {info.getValue() ?? '—'}
      </DS.Text>
    )
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => <ManageCell loungeId={row.original._id} />
  })
]

function ManageCell({ loungeId }: { loungeId: string }) {
  const router = useRouter()
  return (
    <Pressable onPress={() => router.push(`/dashboard/lounges/${loungeId}` as any)} style={styles.manageButton}>
      <DS.Text variant="Mini/Bold" style={{ color: DS.palette['Brand/Maroon'] }}>
        Manage
      </DS.Text>
    </Pressable>
  )
}

export function LoungesTable() {
  const store = useStore()
  const userId = store.session.user?.auth0Id ?? ''
  const userName = store.session.user?.name ?? 'Unknown'
  const [newLoungeName, setNewLoungeName] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  // TODO how is this useful? Queries lounges belonging to dashboard user?
  const lounges = useQuery(api.lounges.queries.getLoungesForUser, userId ? { userId } : 'skip')

  const createLounge = useMutation(api.lounges.mutations.createLounge)

  const table = useReactTable({
    data: (lounges ?? []) as LoungeRow[],
    columns,
    getCoreRowModel: getCoreRowModel()
  })

  const handleCreate = async () => {
    if (!newLoungeName.trim() || !userId) return
    setCreating(true)
    setCreateError(null)
    try {
      await createLounge({ name: newLoungeName.trim(), ownerId: userId, userName })
      setNewLoungeName('')
    } catch (err: any) {
      setCreateError(err.message ?? 'Failed to create lounge')
    } finally {
      setCreating(false)
    }
  }

  return (
    <View style={styles.container}>
      <DS.Text variant="Heading 3/Medium">Lounges</DS.Text>

      {/* Create Lounge */}
      <View style={styles.createRow}>
        <TextInput
          style={styles.input}
          placeholder="New lounge name"
          value={newLoungeName}
          onChangeText={setNewLoungeName}
        />
        <DS.Button
          type="On Light"
          variant="Medium/Primary"
          backgroundColor="Brand/Maroon"
          onPress={handleCreate}
          disabled={creating || !newLoungeName.trim()}
        >
          {creating ? 'Creating...' : 'Create Lounge'}
        </DS.Button>
      </View>
      {createError && (
        <DS.Text variant="Mini/Regular" style={{ color: DS.palette['Utility/Error'] }}>
          {createError}
        </DS.Text>
      )}

      {/* Table */}
      <View style={styles.tableContainer}>
        <View style={styles.tableHeader}>
          {table.getHeaderGroups().map((hg) => (
            <View style={styles.tableRow} key={hg.id}>
              {hg.headers.map((header) => (
                <View style={styles.tableHeaderCell} key={header.id}>
                  <Text style={styles.headerText}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </Text>
                </View>
              ))}
            </View>
          ))}
        </View>
        <View style={styles.tableBody}>
          {table.getRowModel().rows.map((row) => (
            <View style={styles.tableRow} key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <View style={styles.tableCell} key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </View>
              ))}
            </View>
          ))}
          {(lounges ?? []).length === 0 && (
            <View style={styles.emptyRow}>
              <DS.Text variant="Body Small/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>
                No lounges yet.
              </DS.Text>
            </View>
          )}
        </View>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: 24,
    marginBottom: 32,
    borderRadius: 12,
    borderColor: DS.palette['Brand/Light Grey'],
    borderWidth: 1,
    backgroundColor: DS.palette['Global/White'],
    gap: 16
  },
  createRow: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center'
  },
  input: {
    flex: 1,
    height: 40,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    paddingHorizontal: 12,
    backgroundColor: DS.palette['Brand/Cream'],
    fontSize: 14
  },
  tableContainer: {
    borderRadius: 12,
    overflow: 'hidden',
    borderColor: DS.palette['Brand/Light Grey'],
    borderWidth: 1
  },
  tableHeader: {
    backgroundColor: '#eeedea'
  },
  tableBody: {
    backgroundColor: 'white'
  },
  tableRow: {
    flexDirection: 'row'
  },
  tableHeaderCell: {
    flex: 1,
    borderBottomWidth: 2,
    borderBottomColor: '#dee2e6',
    padding: 16
  },
  headerText: {
    fontWeight: '600',
    color: DS.palette['Brand/Dark Grey']
  },
  tableCell: {
    flex: 1,
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef'
  },
  emptyRow: {
    padding: 24,
    alignItems: 'center'
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 99,
    alignSelf: 'flex-start'
  },
  statusActive: {
    backgroundColor: DS.palette['Utility/Success']
  },
  statusInactive: {
    backgroundColor: DS.palette['Brand/Dark Grey']
  },
  manageButton: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Maroon'],
    alignSelf: 'flex-start'
  }
})
